// src/components/BulletinRepartitionProjet.js
// Section "Répartition par projet" à insérer dans l'aperçu bulletin
// Affiche le tableau de coût partagé multi-projets (jusqu'à 5 projets)
// Usage : <BulletinRepartitionProjet repartition={bulletin.repartition_projets} bulletin={bulletin} />

import React, { useState } from 'react';

const fcfa = (v) => {
  const n = parseFloat(v) || 0;
  return n.toLocaleString('fr-CI') + ' FCFA';
};

const pct = (v) => `${parseFloat(v || 0).toFixed(1)}%`;

// Couleurs par projet (jusqu'à 5)
const PROJ_COLORS = ['#1565C0', '#2E7D32', '#E65100', '#6A1B9A', '#00838F'];

export default function BulletinRepartitionProjet({ repartition = [], bulletin = {} }) {
  const [vue, setVue] = useState('resume'); // 'resume' | 'detail'

  if (!repartition || repartition.length === 0) return null;

  const hasMultiple = repartition.length > 1;

  // Totaux de vérification
  const totalBrut    = repartition.reduce((s, r) => s + parseFloat(r.brut_total    || 0), 0);
  const totalNet     = repartition.reduce((s, r) => s + parseFloat(r.net_a_payer   || 0), 0);
  const totalPat     = repartition.reduce((s, r) => s + parseFloat(r.total_patronal|| 0), 0);
  const totalCout    = repartition.reduce((s, r) => s + parseFloat(r.cout_total    || 0), 0);

  return (
    <div style={S.container}>
      {/* ── Titre + toggle vue ── */}
      <div style={S.header}>
        <div style={S.headerLeft}>
          <div style={S.dot} />
          <span style={S.title}>
            RÉPARTITION PAR PROJET — COÛT PARTAGÉ
          </span>
          <span style={S.badge}>{repartition.length} projet{repartition.length > 1 ? 's' : ''}</span>
        </div>
        {hasMultiple && (
          <div style={S.toggle}>
            <button
              style={{ ...S.toggleBtn, ...(vue === 'resume' ? S.toggleActive : {}) }}
              onClick={() => setVue('resume')}>
              Résumé
            </button>
            <button
              style={{ ...S.toggleBtn, ...(vue === 'detail' ? S.toggleActive : {}) }}
              onClick={() => setVue('detail')}>
              Détail complet
            </button>
          </div>
        )}
      </div>

      {/* ── Vue RÉSUMÉ ── */}
      {vue === 'resume' && (
        <>
          {/* Barres de répartition visuelle */}
          <div style={S.barWrapper}>
            {repartition.map((r, i) => (
              <div key={i} style={{ ...S.barSegment, width: pct(r.pourcentage), background: PROJ_COLORS[i] }}
                title={`${r.nom_projet} — ${pct(r.pourcentage)}`} />
            ))}
          </div>

          {/* Tableau résumé */}
          <table style={S.table}>
            <thead>
              <tr style={S.thead}>
                <th style={S.th}>Projet / Bailleur</th>
                <th style={{ ...S.th, textAlign: 'center' }}>Part</th>
                <th style={{ ...S.th, textAlign: 'right' }}>Brut total</th>
                <th style={{ ...S.th, textAlign: 'right' }}>Net à payer</th>
                <th style={{ ...S.th, textAlign: 'right' }}>Ch. patronales</th>
                <th style={{ ...S.th, textAlign: 'right', background: '#1a237e', color: '#fff' }}>Coût total projet</th>
              </tr>
            </thead>
            <tbody>
              {repartition.map((r, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #e8eaf6' }}>
                  <td style={S.td}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 10, height: 10, borderRadius: 2, background: PROJ_COLORS[i], flexShrink: 0 }} />
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 12, color: '#1a237e' }}>{r.nom_projet}</div>
                        {r.bailleur && <div style={{ fontSize: 10, color: '#888' }}>{r.bailleur}</div>}
                      </div>
                    </div>
                  </td>
                  <td style={{ ...S.td, textAlign: 'center' }}>
                    <span style={{ background: PROJ_COLORS[i], color: '#fff', padding: '2px 8px',
                      borderRadius: 12, fontSize: 11, fontWeight: 700 }}>
                      {pct(r.pourcentage)}
                    </span>
                  </td>
                  <td style={{ ...S.td, textAlign: 'right', fontFamily: 'monospace' }}>
                    {fcfa(r.brut_total)}
                  </td>
                  <td style={{ ...S.td, textAlign: 'right', fontWeight: 600, color: '#2E7D32', fontFamily: 'monospace' }}>
                    {fcfa(r.net_a_payer)}
                  </td>
                  <td style={{ ...S.td, textAlign: 'right', color: '#E65100', fontFamily: 'monospace' }}>
                    {fcfa(r.total_patronal)}
                  </td>
                  <td style={{ ...S.td, textAlign: 'right', fontWeight: 800, color: '#1a237e',
                    background: '#e8eaf6', fontFamily: 'monospace' }}>
                    {fcfa(r.cout_total)}
                  </td>
                </tr>
              ))}
            </tbody>
            {hasMultiple && (
              <tfoot>
                <tr style={{ background: '#1a237e', color: '#fff' }}>
                  <td style={{ ...S.td, color: '#fff', fontWeight: 800 }}>TOTAL GLOBAL</td>
                  <td style={{ ...S.td, textAlign: 'center', color: '#fff', fontWeight: 800 }}>100%</td>
                  <td style={{ ...S.td, textAlign: 'right', color: '#fff', fontFamily: 'monospace', fontWeight: 700 }}>
                    {fcfa(totalBrut)}
                  </td>
                  <td style={{ ...S.td, textAlign: 'right', color: '#a5d6a7', fontFamily: 'monospace', fontWeight: 800 }}>
                    {fcfa(totalNet)}
                  </td>
                  <td style={{ ...S.td, textAlign: 'right', color: '#ffcc80', fontFamily: 'monospace' }}>
                    {fcfa(totalPat)}
                  </td>
                  <td style={{ ...S.td, textAlign: 'right', color: '#fff', fontFamily: 'monospace', fontWeight: 900, fontSize: 13 }}>
                    {fcfa(totalCout)}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </>
      )}

      {/* ── Vue DÉTAIL COMPLET ── */}
      {vue === 'detail' && (
        <div style={{ overflowX: 'auto' }}>
          <table style={S.table}>
            <thead>
              <tr style={S.thead}>
                <th style={S.th}>Ligne</th>
                {repartition.map((r, i) => (
                  <th key={i} style={{ ...S.th, textAlign: 'right', minWidth: 130,
                    borderLeft: '2px solid #fff', background: PROJ_COLORS[i] }}>
                    <div style={{ fontSize: 11 }}>{r.nom_projet}</div>
                    <div style={{ fontSize: 10, fontWeight: 400, opacity: 0.85 }}>{pct(r.pourcentage)}</div>
                  </th>
                ))}
                {hasMultiple && (
                  <th style={{ ...S.th, textAlign: 'right', background: '#1a237e', minWidth: 130 }}>
                    TOTAL
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {[
                { label: 'Salaire de base',      key: 'salaire_base',    group: 'gain' },
                { label: 'Sursalaire',            key: 'sursalaire',     group: 'gain' },
                { label: 'Ind. transport',        key: 'transport',      group: 'gain' },
                { label: 'Ind. logement',         key: 'logement',       group: 'gain' },
                { label: 'BRUT TOTAL',            key: 'brut_total',     group: 'subtotal' },
                { label: 'CNPS salarié (6,3%)',   key: 'cnps_sal',       group: 'ret' },
                { label: 'ITS',                   key: 'its',            group: 'ret' },
                { label: 'IGR (1,5%)',            key: 'igr',            group: 'ret' },
                { label: 'Total retenues',        key: 'total_retenues', group: 'subtotal' },
                { label: 'NET À PAYER',           key: 'net_a_payer',    group: 'net' },
                { label: 'CNPS patronal (14%)',   key: 'cnps_pat',       group: 'pat' },
                { label: 'CFCE patronal (2,8%)',  key: 'cfce_pat',       group: 'pat' },
                { label: 'Taxe apprentissage',    key: 'ta_pat',         group: 'pat' },
                { label: 'Total charges patron.', key: 'total_patronal', group: 'subtotal' },
                { label: 'COÛT TOTAL PROJET',     key: 'cout_total',     group: 'total' },
              ].map((row, ri) => {
                const isSubtotal = row.group === 'subtotal';
                const isNet      = row.group === 'net';
                const isTotal    = row.group === 'total';
                const isRet      = row.group === 'ret';
                const isPat      = row.group === 'pat';

                const rowStyle = {
                  background: isTotal    ? '#1a237e'
                             : isNet     ? '#e8f5e9'
                             : isSubtotal? '#f5f5f5'
                             : isPat     ? '#fff8e1'
                             : isRet     ? '#fce4ec'
                             : '#fff',
                  borderBottom: isTotal || isNet ? '2px solid #1a237e' : '1px solid #eee',
                };
                const tdStyle = {
                  padding: '5px 10px', fontSize: 11,
                  color: isTotal ? '#fff' : isNet ? '#1b5e20' : isRet ? '#b71c1c' : '#333',
                  fontWeight: isTotal || isNet || isSubtotal ? 700 : 400,
                };

                return (
                  <tr key={ri} style={rowStyle}>
                    <td style={{ ...tdStyle, fontStyle: isSubtotal ? 'italic' : 'normal' }}>
                      {row.label}
                    </td>
                    {repartition.map((r, i) => (
                      <td key={i} style={{ ...tdStyle, textAlign: 'right',
                        fontFamily: 'monospace', borderLeft: '1px solid #e8eaf6',
                        color: isTotal ? '#fff' : tdStyle.color }}>
                        {parseFloat(r[row.key] || 0) > 0 ? fcfa(r[row.key]) : '—'}
                      </td>
                    ))}
                    {hasMultiple && (
                      <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace',
                        borderLeft: '2px solid #1a237e',
                        background: isTotal ? '#000051' : isNet ? '#c8e6c9' : '#f0f0f0',
                        color: isTotal ? '#fff' : isNet ? '#1b5e20' : '#333' }}>
                        {fcfa(repartition.reduce((s, r) => s + parseFloat(r[row.key] || 0), 0))}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Note de bas de section */}
      <div style={S.note}>
        ℹ️ Répartition calculée sur la base des feuilles de temps du mois.
        La somme des colonnes projet = le bulletin global ci-dessus.
      </div>
    </div>
  );
}

// ── Styles ──────────────────────────────────────────────────
const S = {
  container:   { marginTop: 16, border: '1.5px solid #c5cae9', borderRadius: 8, overflow: 'hidden' },
  header:      { display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                 padding: '10px 14px', background: '#e8eaf6', borderBottom: '1px solid #c5cae9' },
  headerLeft:  { display: 'flex', alignItems: 'center', gap: 10 },
  dot:         { width: 10, height: 10, borderRadius: '50%', background: '#1a237e' },
  title:       { fontWeight: 800, fontSize: 12, color: '#1a237e', letterSpacing: 0.5 },
  badge:       { background: '#1a237e', color: '#fff', borderRadius: 20,
                 padding: '2px 10px', fontSize: 11, fontWeight: 600 },
  toggle:      { display: 'flex', gap: 4 },
  toggleBtn:   { padding: '4px 12px', border: '1px solid #9fa8da', borderRadius: 20,
                 background: '#fff', color: '#3949ab', fontSize: 11, cursor: 'pointer' },
  toggleActive:{ background: '#1a237e', color: '#fff', border: '1px solid #1a237e' },
  barWrapper:  { display: 'flex', height: 8, overflow: 'hidden' },
  barSegment:  { height: '100%', transition: 'width .3s' },
  table:       { width: '100%', borderCollapse: 'collapse', fontSize: 12 },
  thead:       { background: '#3949ab' },
  th:          { padding: '7px 10px', color: '#fff', fontWeight: 700,
                 textAlign: 'left', fontSize: 11 },
  td:          { padding: '6px 10px' },
  note:        { padding: '8px 14px', background: '#f9fafb', borderTop: '1px solid #e8eaf6',
                 fontSize: 11, color: '#888', fontStyle: 'italic' },
};
